import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useAdmin } from "../../../context/AdminContext";
import { useNavigate } from "react-router-dom";

const MAX_NAME = 80;
const MAX_DESC = 800;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_OTHER_IMAGES = 5;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const CATEGORIES = [
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const createEmptySpecification = () => ({ feature: "", value: "" });

const normalizeSpecifications = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [createEmptySpecification()];
  }

  return items.map((item) => ({
    feature: typeof item?.feature === "string" ? item.feature : "",
    value: typeof item?.value === "string" ? item.value : "",
  }));
};

const getInitialFormState = (prefill) => ({
  name: typeof prefill?.name === "string" ? prefill.name : "",
  description:
    typeof prefill?.description === "string" ? prefill.description : "",
  category: typeof prefill?.category === "string" ? prefill.category : "",
  starred: Boolean(prefill?.starred),
  coverPreview:
    typeof prefill?.coverImage === "string" ? prefill.coverImage : null,
  existingImages: Array.isArray(prefill?.otherImages)
    ? prefill.otherImages
    : [],
  specifications: normalizeSpecifications(prefill?.specifications),
});

const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

const AddProduct = ({ prefill = null, productId = null, onSuccess }) => {
  const { token } = useAdmin();
  const navigate = useNavigate();
  const formState = getInitialFormState(prefill);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [name, setName] = useState(formState.name);
  const [description, setDescription] = useState(formState.description);
  const [category, setCategory] = useState(formState.category);
  const [starred, setStarred] = useState(formState.starred);

  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(formState.coverPreview);

  const [existingImages, setExistingImages] = useState(
    formState.existingImages,
  );
  const [removedImages, setRemovedImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [uploadProgress, setUploadProgress] = useState({ global: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [specifications, setSpecifications] = useState(
    formState.specifications,
  );

  useEffect(() => {
    const nextState = getInitialFormState(prefill);

    setName(nextState.name);
    setDescription(nextState.description);
    setCategory(nextState.category);
    setStarred(nextState.starred);
    setCoverImage(null);
    setCoverPreview(nextState.coverPreview);
    setExistingImages(nextState.existingImages);
    setRemovedImages([]);
    setNewImages([]);
    setUploadProgress({ global: 0 });
    setError("");
    setSpecifications(nextState.specifications);
  }, [prefill]);

  const validateImage = (file) => {
    if (!file) return "No file selected";

    const validExt = /\.(jpg|jpeg|png|webp)$/i.test(file.name);
    const validMime = !file.type || ACCEPTED_IMAGE_TYPES.includes(file.type);

    if (!validExt || !validMime) return "Only images allowed";
    if (file.size > MAX_IMAGE_SIZE) return "Image must be under 2MB";

    return null;
  };

  const revokeImagePreview = (preview) => {
    if (isBlobUrl(preview)) {
      URL.revokeObjectURL(preview);
    }
  };

  const compressImage = async (file) => {
    try {
      return await imageCompression(file, {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
    } catch {
      return file;
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    const err = validateImage(file);
    if (err) return setError(err);

    const compressed = await compressImage(file);
    revokeImagePreview(coverPreview);

    setCoverImage(compressed);
    setCoverPreview(URL.createObjectURL(compressed));
    setError("");
  };

  const removeCoverImage = () => {
    if (!coverImage && productId) {
      return;
    }

    revokeImagePreview(coverPreview);
    setCoverImage(null);
    setCoverPreview(productId ? formState.coverPreview : null);
    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  };

  const handleNewImages = async (files) => {
    const selectedFiles = files.filter((file) => file?.type || file?.name);
    const total =
      existingImages.length + newImages.length + selectedFiles.length;

    if (total > MAX_OTHER_IMAGES) {
      return setError(`Max ${MAX_OTHER_IMAGES} images allowed`);
    }

    const processed = [];

    for (const file of selectedFiles) {
      const err = validateImage(file);
      if (err) return setError(err);

      const compressed = await compressImage(file);

      processed.push({
        file: compressed,
        preview: URL.createObjectURL(compressed),
      });
    }

    setNewImages((prev) => [...prev, ...processed]);
    setError("");
  };

  const handleOtherImages = (e) => {
    handleNewImages([...e.target.files]);
    e.target.value = "";
  };

  const removeNewImage = (index) => {
    setNewImages((prev) =>
      prev.filter((img, i) => {
        if (i === index) {
          revokeImagePreview(img.preview);
          return false;
        }
        return true;
      }),
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleNewImages([...e.dataTransfer.files]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleSpecificationChange = (index, field, value) => {
    setSpecifications((prev) =>
      prev.map((spec, i) => (i === index ? { ...spec, [field]: value } : spec)),
    );
  };

  const addSpecification = () => {
    setSpecifications((prev) => [...prev, { feature: "", value: "" }]);
  };

  const removeSpecification = (index) => {
    setSpecifications((prev) => {
      if (prev.length === 1) return [createEmptySpecification()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (img) => {
    setExistingImages((prev) => prev.filter((i) => i !== img));
    setRemovedImages((prev) => (prev.includes(img) ? prev : [...prev, img]));
  };

  const resetForm = () => {
    revokeImagePreview(coverPreview);
    newImages.forEach((img) => revokeImagePreview(img.preview));

    setName("");
    setDescription("");
    setCategory("");
    setStarred(false);
    setCoverImage(null);
    setCoverPreview(null);
    setExistingImages([]);
    setRemovedImages([]);
    setNewImages([]);
    setUploadProgress({ global: 0 });
    setError("");
    setSpecifications([createEmptySpecification()]);

    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }

    if (galleryInputRef.current) {
      galleryInputRef.current.value = "";
    }
  };

  useEffect(() => {
    return () => {
      revokeImagePreview(coverPreview);
      newImages.forEach((img) => revokeImagePreview(img.preview));
    };
  }, [coverPreview, newImages]);

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const hasCover = coverImage || coverPreview;

    if (!trimmedName) return "Product name is required";
    if (!trimmedDescription) return "Description is required";
    if (!category || !CATEGORIES.includes(category))
      return "Select a valid category";
    if (!hasCover) return "Cover image is required";

    const hasIncompleteSpecification = specifications.some((spec) => {
      const feature = spec.feature.trim();
      const value = spec.value.trim();
      return (feature && !value) || (!feature && value);
    });

    if (hasIncompleteSpecification) {
      return "Each specification must include both a feature and a value";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setUploadProgress({ global: 0 });

    try {
      const authToken = token || localStorage.getItem("adminToken");
      if (!authToken) throw new Error("Not authenticated");

      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      const cleanedSpecifications = specifications
        .map((spec) => ({
          feature: spec.feature.trim(),
          value: spec.value.trim(),
        }))
        .filter((spec) => spec.feature && spec.value);

      const formData = new FormData();

      formData.append("name", trimmedName);
      formData.append("description", trimmedDescription);
      formData.append("category", category);
      formData.append("starred", String(starred));
      formData.append("specifications", JSON.stringify(cleanedSpecifications));
      formData.append("removedImages", JSON.stringify(removedImages));

      if (coverImage) formData.append("coverImage", coverImage);

      newImages.forEach((img) => formData.append("otherImages", img.file));

      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress({ global: percent });
        },
      };

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

      if (productId) {
        await axios.put(`${API}/api/products/${productId}`, formData, config);
      } else {
        await axios.post(`${API}/api/products`, formData, config);
      }
      alert("Saved successfully!");
      onSuccess?.();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalImagesUsed = existingImages.length + newImages.length;
  const remainingImages = MAX_OTHER_IMAGES - totalImagesUsed;
  const canRemoveCoverImage = Boolean(
    coverImage || (!productId && coverPreview),
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white p-6">
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard")}
        className="mb-6 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
      >
        ← Back to Dashboard
      </button>

      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-6">
        {/* HEADER */}
        <div className="bg-white/10 p-5 rounded-2xl border border-white/10">
          <h1 className="text-2xl font-semibold">
            {productId ? "Edit Product" : "Add New Product"}
          </h1>
          <p className="text-gray-400 text-sm">
            Fill product details and upload images
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-red-500/10 border border-red-500 text-red-300 p-3 rounded-lg"
          >
            {error}
          </div>
        )}

        {/* BASIC INFO */}
        <div className="bg-white/10 p-6 rounded-2xl space-y-4 border border-white/10">
          <div className="grid gap-2">
            <label
              htmlFor="product-name"
              className="text-sm font-medium text-gray-200"
            >
              Product Name
            </label>
            <input
              id="product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              placeholder="Product Name"
              autoComplete="off"
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_NAME - name.length} characters remaining
            </div>
          </div>

          <div className="grid gap-2">
            <label
              htmlFor="product-category"
              className="text-sm font-medium text-gray-200"
            >
              Category
            </label>
            <select
              id="product-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="w-full p-3 rounded-lg bg-gray-800 border border-white/10 focus:outline-none focus:border-green-500"
            >
              <option value="">Select Category</option>
              {CATEGORIES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          {/* DESCRIPTION */}
          <div className="grid gap-2">
            <label
              htmlFor="product-description"
              className="text-sm font-medium text-gray-200"
            >
              Description
            </label>
            <textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_DESC}
              rows={4}
              placeholder="Product description..."
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_DESC - description.length} characters remaining
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={starred}
              onChange={(e) => setStarred(e.target.checked)}
            />
            Feature on homepage
          </label>
        </div>

        {/* COVER IMAGE */}
        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-3">
          <h2 className="font-semibold">Cover Image</h2>
          <p className="text-sm text-gray-400">
            Upload the main product image shown on listings and detail pages.
          </p>

          <input
            ref={coverInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            onChange={handleCoverChange}
          />

          {coverPreview && (
            <div className="relative w-40">
              <img
                src={coverPreview}
                alt="cover"
                className="w-40 h-40 object-cover rounded-xl border border-white/10"
              />
              {canRemoveCoverImage && (
                <button
                  type="button"
                  onClick={removeCoverImage}
                  className="absolute top-2 right-2 bg-red-600 w-7 h-7 rounded-full"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {/* IMAGE UPLOAD */}
        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">Product Images</h2>
              <p className="text-sm text-gray-400 mt-1">
                Add supporting images for gallery view. JPG, PNG, and WebP are
                supported.
              </p>
            </div>
            <span className="text-xs text-gray-400 shrink-0">
              {remainingImages} slots remaining
            </span>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-white/20 p-8 rounded-xl text-center text-gray-300 hover:border-green-500 transition"
          >
            Drag & drop images here or upload below
            <input
              ref={galleryInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={handleOtherImages}
              className="mt-3 block mx-auto"
            />
          </div>

          {/* NEW IMAGES */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {newImages.map((img, i) => (
              <div key={i} className="relative">
                <img
                  src={img.preview}
                  className="w-full h-24 object-cover rounded-lg"
                  alt={`product ${i + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeNewImage(i)}
                  className="absolute top-1 right-1 bg-black/70 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* EXISTING IMAGES */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {existingImages.map((img, i) => (
              <div key={i} className="relative">
                <img
                  src={img}
                  className="w-full h-24 object-cover rounded-lg"
                  alt={`product ${i + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(img)}
                  className="absolute top-1 right-1 bg-red-600 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* PROGRESS */}
          {uploadProgress.global > 0 && (
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${uploadProgress.global}%` }}
              />
            </div>
          )}
        </div>

        {/* SPECIFICATIONS */}
        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold">Specifications</h2>
              <p className="text-sm text-gray-400 mt-1">
                Add product features such as material, size, finish, or usage.
              </p>
            </div>
            <button
              type="button"
              onClick={addSpecification}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm"
            >
              + Add Spec
            </button>
          </div>

          <div className="space-y-3">
            {specifications.map((spec, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start"
              >
                <input
                  value={spec.feature}
                  onChange={(e) =>
                    handleSpecificationChange(index, "feature", e.target.value)
                  }
                  placeholder="Feature"
                  maxLength={60}
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
                />
                <input
                  value={spec.value}
                  onChange={(e) =>
                    handleSpecificationChange(index, "value", e.target.value)
                  }
                  placeholder="Value"
                  maxLength={120}
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
                />
                <button
                  type="button"
                  onClick={() => removeSpecification(index)}
                  className="px-4 py-3 rounded-lg bg-red-500/15 text-red-300 hover:bg-red-500/25 transition"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed py-3 rounded-xl font-semibold"
        >
          {loading ? "Saving..." : productId ? "Update Product" : "Add Product"}
        </button>
      </form>
    </div>
  );
};

export default AddProduct;
